if (!customElements.get('cart-shipping-calculator')) {
  class CartShippingCalculator extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.form = this.querySelector('.cart-shipping-calculator__form');
      this.input = this.querySelector('.cart-shipping-calculator__input');
      this.button = this.querySelector('.cart-shipping-calculator__button');
      this.status = this.querySelector('.cart-shipping-calculator__status');
      this.results = this.querySelector('.cart-shipping-calculator__results');
      this.options = this.querySelector('.cart-shipping-calculator__options');
      this.closeButton = this.querySelector('.cart-shipping-calculator__close');

      if (!this.form) return;

      this.dataset.initialized = 'true';
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.calculate();
      });
      this.input.addEventListener('input', () => this.formatCep());
      this.closeButton?.addEventListener('click', () => {
        this.results.hidden = true;
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    formatCep() {
      const digits = this.input.value.replace(/\D/g, '').slice(0, 8);
      this.input.value = digits.length > 5
        ? `${digits.slice(0, 5)}-${digits.slice(5)}`
        : digits;
    }

    async calculate() {
      const cep = this.input.value.replace(/\D/g, '');

      if (cep.length !== 8) {
        this.showStatus('Por favor, insira um CEP válido com 8 dígitos.', 'error');
        this.input.focus();
        return;
      }

      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;
      this.setLoading(true);
      this.results.hidden = true;

      try {
        const cart = await this.fetchCart(controller.signal);
        const items = cart.items.filter((item) => item.requires_shipping !== false);

        if (!items.length) {
          throw new Error('Seu carrinho não possui produtos para entrega.');
        }

        const rates = await this.fetchRates(cep, cart, items, controller.signal);
        this.renderRates(rates);
        this.showStatus('', 'success');
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.showStatus(
          error.message || 'Não foi possível calcular o frete. Tente novamente.',
          'error'
        );
      } finally {
        if (this.abortController === controller) this.setLoading(false);
      }
    }

    async fetchCart(signal) {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        signal
      });

      if (!response.ok) throw new Error('Não foi possível consultar o carrinho.');
      return response.json();
    }

    async fetchRates(cep, cart, items, signal) {
      const response = await fetch(this.dataset.quoteUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destination_cep: cep,
          invoice_value: cart.total_price / 100,
          items: items.map((item) => ({
            sku: item.sku || String(item.variant_id),
            quantity: item.quantity,
            height: 10,
            length: 10,
            width: 10,
            weight: (Number(item.grams) || 1000) / 1000
          }))
        }),
        signal
      });

      if (!response.ok) throw new Error('Não foi possível obter as opções de frete.');

      const data = await response.json();
      if (!data?.services || !Array.isArray(data.services)) {
        throw new Error('A transportadora retornou uma resposta inválida.');
      }

      const rates = data.services
        .filter((service) => !service.error)
        .map((service) => {
          const carrier = service.carrier || 'Transportadora';
          const serviceName = service.service_description || carrier;
          const price = Number(String(service.price || 0).replace(',', '.'));
          return {
            name: serviceName !== carrier ? `${carrier} - ${serviceName}` : carrier,
            price: Number.isFinite(price) ? price : 0,
            days: Number.parseInt(service.delivery_days, 10)
          };
        });

      if (!rates.length) {
        throw new Error('Nenhuma opção de frete está disponível para este CEP.');
      }

      return rates;
    }

    renderRates(rates) {
      this.options.replaceChildren();

      rates.forEach((rate) => {
        const option = document.createElement('div');
        option.className = 'cart-shipping-calculator__option';

        const info = document.createElement('div');
        info.className = 'cart-shipping-calculator__option-info';

        const name = document.createElement('span');
        name.className = 'cart-shipping-calculator__option-name';
        name.textContent = rate.name;

        const time = document.createElement('span');
        time.className = 'cart-shipping-calculator__option-time';
        time.textContent = this.formatDeliveryTime(rate.days);

        const price = document.createElement('strong');
        price.className = 'cart-shipping-calculator__option-price';
        price.textContent = rate.price === 0
          ? 'GRÁTIS'
          : rate.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

        info.append(name, time);
        option.append(info, price);
        this.options.append(option);
      });

      this.results.hidden = false;
    }

    formatDeliveryTime(days) {
      if (!Number.isFinite(days) || days < 0) return 'Consultar prazo';

      const handlingDays = Number.parseInt(this.dataset.handlingDays, 10) || 0;
      const totalDays = days + handlingDays;
      const deliveryDate = new Date();
      let addedDays = 0;
      while (addedDays < totalDays) {
        deliveryDate.setDate(deliveryDate.getDate() + 1);
        if (deliveryDate.getDay() !== 0 && deliveryDate.getDay() !== 6) addedDays += 1;
      }

      const formattedDate = new Intl.DateTimeFormat('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      }).format(deliveryDate);

      return `Chegará até ${formattedDate}`;
    }

    setLoading(isLoading) {
      this.button.disabled = isLoading;
      this.input.disabled = isLoading;
      this.button.textContent = isLoading ? 'Calculando...' : 'Calcular';

      if (isLoading) this.showStatus('Consultando opções de entrega...', 'loading');
    }

    showStatus(message, type) {
      this.status.textContent = message;
      this.status.className = `cart-shipping-calculator__status cart-shipping-calculator__status--${type}`;
      this.status.hidden = !message;
    }
  }

  customElements.define('cart-shipping-calculator', CartShippingCalculator);
}
